import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import jQuery from 'jquery'
import VuexORM from '@vuex-orm/core'
import VuexORMAxios from '@vuex-orm/plugin-axios'
import { addModelsToDatabase, User, XConfig, addRecordsToStore } from '@/models'
import { get, flatten } from 'lodash'

Vue.use(Vuex)

// Set up custom axios instance prefixed with /api/
const axiosInstance = axios.create({
  baseURL: '/api/'
})
axiosInstance.interceptors.request.use((config) => {
  config.headers = {
    ...(config.headers || {}),
    'x-require-auth': 'yes' // Tell the api to return a 401 on endpoints that don't require auth if not authed
  }
  return config
})
axiosInstance.interceptors.response.use((response) => {
  if (response && response.data && response.data.relationships) {
    addRecordsToStore(response.data.relationships, { force: true })
  }
  return response
}, (error) => {
  // Global error handler
  let message = error.message
  if (error.response) {
    message = get(error.response, 'data.message') || get(error.response, 'data.error') || get(error.response, 'data') || message
    if (get(error.response, 'data.errors')) {
      message = message + ' ' + flatten(Object.values(error.response.data.errors))
    }
  }
  if (!get(error, 'config.hideErrors')) {
    if ((get(error.response, 'status') || '') + '' === '401') {
      store.commit('setRequireAuth', true)
    } else {
      store.commit('setAppError', message)
    }
  }
  throw error
})
axiosInstance.CancelToken = axios.CancelToken
Vue.prototype.$axios = Vue.$axios = axiosInstance


// Set up VuexORM database
VuexORM.use(VuexORMAxios, {
  axios: axiosInstance
})
const database = new VuexORM.Database()
addModelsToDatabase(database)

Vue.mixin({
  methods: {
    $http (params) {
      return axiosInstance(params)
    }
  }
})

const store = new Vuex.Store({
  plugins: [VuexORM.install(database)],
  state: {
    appError: null,
    appDrawerLeft: false,
    sessionLoading: false,
    userId: null,
    cart: null,
    loggingOut: false,
    requireAuth: false,
    returnsDialog: false,
    supportDialog: false
  },
  getters: {
    user (state) {
      if (state.userId) {
        return User.find(state.userId)
      }
      return null
    },
    rewards (state, getters) {
      return getters.user && getters.user.rewards && getters.user.rewards.raw
    }
  },
  mutations: {
    setAppError (state, v) {
      state.appError = v
    },
    setAppDrawerLeft (state, value) {
      state.appDrawerLeft = value
    },
    setUserId (state, userId) {
      state.userId = userId
    },
    setSessionLoading (state, loading) {
      state.sessionLoading = loading
    },
    setCart (state, cart) {
      state.cart = cart || {}
    },
    setLoggingOut (state, v) {
      state.loggingOut = v
    },
    setRequireAuth (state, v) {
      state.requireAuth = v
    },
    setReturnsDialog (state, v) {
      state.returnsDialog = v
    },
    setSupportDialog (state, v) {
      state.supportDialog = v
    }
  },
  actions: {
    async getSession ({ commit }) {
      commit('setSessionLoading', true)
      try {
        const [
          userResponse
        ] = await Promise.all([
          new Promise((resolve, reject) => {
            jQuery.ajax({
              method: 'POST',
              url: '/backend/api/user.php',
              data: {
                mode: 'get_session'
              },
              success: (data) => {
                try {
                  resolve(JSON.parse(data))
                } catch (e) {
                  resolve(data)
                }
              },
              error: (xhr, status, error) => {
                reject(error)
              }
            })
          }),
          XConfig.fetchMany()
        ])

        // Set the user to the store
        if (userResponse && userResponse.data) {
          const data = userResponse.data
          if (data.user && !Array.isArray(data.user)) {
            User.insert({ data: data.user })
            commit('setUserId', data.user.id)
            commit('setRequireAuth', false)
          } else {
            commit('setRequireAuth', true)
          }
          commit('setCart', data.cart && !Array.isArray(data.cart) ? data.cart : null)
        }
        commit('setSessionLoading', false)
      } catch (e) {
        commit('setSessionLoading', false)
        commit('setRequireAuth', true)
        throw e
      }
    },
    async logout ({ commit, dispatch }) {
      commit('setLoggingOut', true)
      try {
        await new Promise((resolve, reject) => {
          jQuery.ajax({
            method: 'POST',
            url: '/backend/api/user.php',
            data: {
              mode: 'logout'
            },
            success: (data) => {
              try {
                resolve({ data: JSON.parse(data) })
              } catch (e) {
                resolve({ data })
              }
            },
            error: (xhr, status, error) => {
              reject(error)
            }
          })
        })
        await dispatch('entities/deleteAll')
        window.location = '/'
      } catch (e) {
        commit('setLoggingOut', false)
        throw e
      }
    }
  }
})

window.$store = store

export default store